<template>
    <el-card class="yellow-widget">
        <div v-if="$slots.header" slot="header">
            <slot name="header"></slot>
        </div>
        <div class="row mt-4 mb-1 ml-2" v-if="totalSubmissions">
            <div class="col-2">
                <div class="row mix-blend-mode-multiple">
                    <div class="col-12 p-0 d-flex align-items-center lender-logo"
                         v-for="(logo, index) in datasets.logos" :key="index">
                        <img v-if="logo" :src="logo" class="w-100"/>
                        <div v-else class="mx-auto lender-text">{{ datasets.labels[index] }}</div>
                    </div>
                </div>
            </div>
            <horizontal-bar-chart ref="lenderSubs" v-if="loaded" :data="datasets" :options="options"
                                  class="col-10 w-100"
                                  :style="{'height': ((totalLenders * 40) + 75) + 'px'}"></horizontal-bar-chart>
        </div>
        <div class="row col-12 my-2" v-else>
            <span class="text-muted small">No results available</span>
        </div>
    </el-card>
</template>


<script>
import HorizontalBarChart from "./charts/HorizontalBarChart";

export default {
    components: {
        HorizontalBarChart
    },
    props: {
        token: {
            type: String,
            required: true
        },
        dateScope: {
            type: Object,
            required: true
        },
        teamId: {
            type: [Number, String],
            required: false
        },
        brokerId: {
            type: Number,
            required: false
        },
    },
    data() {
        return {
            lenderSubmissions: [],
            labels: [],
            datasets: [],
            loaded: false,
            options: {
                responsive: true,
                maintainAspectRatio: false,
                height: 300,
                scales: {
                    xAxes: [{
                        stacked: true,
                        ticks: {
                            precision: 0,
                            fontFamily: "Poppins",
                            padding: 5,
                            boxWidth: 15,
                            fontSize: 12,
                            fontColor: "#000",
                            fontStyle: "normal",
                            callback: function (value, index, values) {
                                return Number(value).toLocaleString("en-AU");
                            }.bind(this)
                        }
                    }],
                    yAxes: [{
                        stacked: true,
                        ticks: {
                            display: false,
                            fontFamily: "Poppins",
                            padding: 5,
                            boxWidth: 15,
                            fontSize: 12,
                            fontColor: "#000",
                            fontStyle: "normal",
                            callback: function (value, index, values) {
                                if (String(value).length > 15)
                                    return String(value).substring(0, 13) + '...'
                                else
                                    return String(value);
                            }
                        }
                    }]
                },
                legend: {
                    display: true,
                    position: 'bottom',
                    labels: {
                        fontFamily: "Poppins",
                        padding: 10,
                        boxWidth: 13,
                        fontSize: 12,
                        fontColor: "#000",
                        fontStyle: "normal"
                    }
                },
                tooltips: {
                    mode: 'label',
                    titleFontFamily: "Poppins",
                    titleFontSize: 13,
                    titleMarginBottom: 15,
                    bodyFontFamily: "Poppins",
                    bodyFontSize: 13,
                    bodySpacing: 15,
                    footerFontFamily: "Poppins",
                    footerFontStyle: "normal",
                    footerFontSize: 13,
                    footerMarginTop: 15,
                    cornerRadius: 0,
                    xPadding: 15,
                    yPadding: 15,
                    backgroundColor: 'rgba(8,7,48,0.8)',
                    callbacks: {
                        title: function (t, d) {
                            var idx = t[0].index;
                            return d.labels[idx];
                        },
                        label: function (t, d) {
                            var dstLabel = d.datasets[t.datasetIndex].label.replace(/ .*/, '');
                            var xLabel = t.xLabel;
                            return ' ' + dstLabel + ': ' + Number(xLabel).toLocaleString("en-AU");
                        }.bind(this),
                        footer: function (t, d) {
                            return 'TOTAL: ' + (Number(t[0].value) + Number(t[1].value)).toLocaleString("en-AU");
                        }
                    }
                }
            }
        };
    },
    mounted() {
        this.refreshData();
    },
    created() {
        this.$bus.$on("refreshed-data", value => {
            this.refreshData();
        });
    },
    computed: {
        totalSubmissions() {
            return this.lenderSubmissions.values.total_subs
                ? this.lenderSubmissions.values.total_subs.reduce(function (a, b) {
                    return a + Number(b);
                }, 0)
                : 0;
        },
        totalLenders() {
            return this.lenderSubmissions.labels.length || 0;
        }
    },
    watch: {
        dateScope: {
            handler(newVal, oldVal) {
                this.refreshData();
            },
            deep: true
        },
        teamId: {
            handler(newVal, oldVal) {
                this.refreshData();
            },
            deep: true
        },
        brokerId: {
            handler(newVal, oldVal) {
                this.refreshData();
            },
            deep: true
        }
    },
    methods: {
        refreshData() {
            this.getSubmissionsByLender();
        },
        async getSubmissionsByLender() {
            // try {
            // this.loaded = false;
            let res = await axios.get(`/api/v2/lender-submissions`, {
                headers: {
                    Authorization: `Bearer ${this.token}`
                },
                params: {
                    startDate: this.$moment(this.dateScope.startDate).format('YYYY-MM-DD'),
                    endDate: this.$moment(this.dateScope.endDate).format('YYYY-MM-DD'),
                    teamId: this.teamId,
                    brokerId: this.brokerId
                }
            });

            this.lenderSubmissions = res.data;

            let labels = Object.values(this.lenderSubmissions.labels);
            let logos = Object.values(this.lenderSubmissions.logos);

            this.datasets = {
                datasets: [{
                    label: 'API Submission',
                    backgroundColor: '#4CAF50',
                    data: Object.values(this.lenderSubmissions.values.api_subs)
                }, {
                    label: 'Manual Submission',
                    backgroundColor: '#e12300',
                    data: Object.values(this.lenderSubmissions.values.non_api_subs)
                }],
                labels: labels,
                logos: logos
            };

            this.loaded = true;

        },
        commarize(val, min) {
            min = min || 1e3;
            // Alter numbers larger than 1k
            if (val >= min) {
                var units = ["k", "M", "B", "T"];

                var order = Math.floor(Math.log(val) / Math.log(1000));

                var unitname = units[(order - 1)];
                var num = Math.floor(val / 1000 ** order);

                if (String(num).length == 1) {
                    num = Number(val / 1000 ** order).toFixed(1);
                }
                return num + unitname;
            }
            return val.toLocaleString();
        }
    }
};
</script>

<style scoped>

.lender-logo {
    height: 42px;
    background-position: center center;
    background-repeat: no-repeat;
    background-size: contain;
}

.lender-logo .lender-text {
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

</style>
